import { Flex, Heading, Box, Button, useDisclosure } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  date as commonDate,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';

import { MeshQueryTable } from './MeshQueryTable';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';
import { SlugToNameProvider } from '~/packages/dataPipeline/hooks/slugToNameContext';
import { UploadDrawer } from '~/packages/dataPipeline/ui/uploads/UploadDrawer';

const getDateFromParam = (param: string | null, fallbackDate: Date) => {
  try {
    if (commonTypeguards.isFullDateStr(param)) {
      return commonDate.fullDateStrToDate(param);
    }
  } catch {
    // malformed params should not cause the page to crash
  }
  return fallbackDate;
};

// get the dates from the URL params if provided.
// otherwise, default to the last 7 days.
const getDefaultDates = (search: string) => {
  const currentParams = new URLSearchParams(search);
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  const initialStartDate = getDateFromParam(
    currentParams.get('startDate'),
    sevenDaysAgo,
  );
  const initialEndDate = getDateFromParam(currentParams.get('endDate'), today);

  return [initialStartDate, initialEndDate];
};

export const DataPipelineList = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const [initialStartDate, initialEndDate] = getDefaultDates(location.search);
  const [{ startDate, endDate }, setDateRange] = React.useState<{
    startDate: Date;
    endDate: Date;
  }>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });

  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('startDate', startDate.toISOString().split('T')[0]);
    params.set('endDate', endDate.toISOString().split('T')[0]);
    navigate(`?${params.toString()}`);
  }, [startDate, endDate, navigate]);

  return (
    <SlugToNameProvider organizationId={organizationId}>
      <Flex justify="space-between" alignItems="center" mb={4}>
        <Box>
          <Heading as="h1" size="lg" mb={4}>
            Data Pipelines
          </Heading>
          <FullDateRangePicker
            onSelectDateRange={(newDateRange) => {
              const [newStartDate, newEndDate] = newDateRange;
              if (newStartDate && newEndDate) {
                setDateRange({
                  startDate: newStartDate,
                  endDate: newEndDate,
                });
              }
            }}
            currentDateRange={{ startDate, endDate }}
            w="md"
          />
        </Box>
        <Button onClick={onOpen} colorScheme="v2-aurora" variant="secondary">
          Generate Report
        </Button>
      </Flex>
      <MeshQueryTable
        organizationId={organizationId}
        startDate={startDate}
        endDate={endDate}
        // every time drawer is closed, we should refetch the meshRuns
        // as the drawer may have initiated a new meshRun
        shouldRefetch={!isOpen}
      />
      {isOpen && (
        <UploadDrawer organizationId={organizationId} onClose={onClose} />
      )}
    </SlugToNameProvider>
  );
};
